import React, { useEffect, useRef } from 'react'
import PagesTitle from '../../widgets/pages_title/pages_title'
import './problem_screen.css'
import CreateProblemWidget from './create_problem/create_problem';
import AppBarWidget from '../../widgets/app_bar/app_bar';
import { useSelector, useDispatch } from 'react-redux';
import ProblemView from './problem_view/problem_view';
import { showAlert } from '../../functions/show_alert';
import { Navigate } from 'react-router-dom';
import { selectEvent } from '../../redux/project/projects';

export default function ProblemScreen() {
    const state = useSelector(state => state.projectSlice.projectSelected);
    const selectedEvent = state?.selectedEvent;
    const dispatch = useDispatch();
    const eventProblemMapRef = useRef(new Map());

    // Track the current project ID to detect project changes
    const prevProjectIdRef = useRef(state?.id);

    // Load event problem map from localStorage when project or selected event changes
    useEffect(() => {
        const getStorageKey = () => `eventProblems_${state?.id || 'default'}`;

        const getEventProblemMap = () => {
            try {
                const stored = localStorage.getItem(getStorageKey());
                if (stored) {
                    const parsed = JSON.parse(stored);
                    // Handle both array format (from Array.from) and object format
                    if (Array.isArray(parsed)) {
                        return new Map(parsed);
                    } else if (typeof parsed === 'object') {
                        return new Map(Object.entries(parsed));
                    }
                }
                return new Map();
            } catch (error) {
                console.error('Error parsing event problem map:', error);
                return new Map();
            }
        };

        // If project changed, reset the map
        if (prevProjectIdRef.current !== state?.id) {
            prevProjectIdRef.current = state?.id;
            eventProblemMapRef.current = new Map();
        }
        // Always reload the map to ensure we have the latest data
        eventProblemMapRef.current = getEventProblemMap();
    }, [selectedEvent?.id, state?.id, state?.problem]);

    // Ensure selectedEvent is properly set from events array if it exists but might be stale
    useEffect(() => {
        if (selectedEvent?.id && state?.events && Array.isArray(state.events)) {
            // Verify that selectedEvent matches the event in the events array
            const eventInArray = state.events.find(e => e.id === selectedEvent.id);
            if (eventInArray && (eventInArray.name !== selectedEvent.name ||
                eventInArray.negative_function_exluded !== selectedEvent.negative_function_exluded)) {
                // If the event in the array has different data, re-select it to get the latest data
                dispatch(selectEvent({ eventId: selectedEvent.id }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEvent?.id, state?.events, dispatch]);

    // Reload localStorage mapping when problems are added to Redux (in case a problem was just created)
    useEffect(() => {
        if (state?.problem && Array.isArray(state.problem) && selectedEvent?.id) {
            // Reload the localStorage mapping to ensure we have the latest data
            const getStorageKey = () => `eventProblems_${state?.id || 'default'}`;
            try {
                const stored = localStorage.getItem(getStorageKey());
                if (stored) {
                    const parsed = JSON.parse(stored);
                    const map = Array.isArray(parsed) ? new Map(parsed) : new Map(Object.entries(parsed));
                    eventProblemMapRef.current = map;
                }
            } catch (error) {
                console.error('Error reloading event problem map:', error);
            }
        }
    }, [state?.problem, selectedEvent?.id, state?.id]);

    // Ensure an event is selected - redirect if not
    if (!selectedEvent) {
        showAlert(false, 'Select an event first');
        return <Navigate to={'/event'} />
    }

    // Double-check: Ensure selectedEvent is actually from the events array and not stale
    // If events array exists, verify selectedEvent is in it and use the latest version
    const verifiedSelectedEvent = state?.events && Array.isArray(state.events) && selectedEvent?.id
        ? state.events.find(e => e.id === selectedEvent.id) || selectedEvent
        : selectedEvent;

    // Get the problem ID that was created for this event (from localStorage, if available)
    const getProblemIdForEvent = () => {
        if (!verifiedSelectedEvent?.id) return null;

        // Check if the mapping exists in the ref
        if (eventProblemMapRef.current.has(verifiedSelectedEvent.id)) {
            return eventProblemMapRef.current.get(verifiedSelectedEvent.id);
        }

        // If not in ref, try to reload from localStorage (in case it was just saved)
        const getStorageKey = () => `eventProblems_${state?.id || 'default'}`;
        try {
            const stored = localStorage.getItem(getStorageKey());
            if (stored) {
                const parsed = JSON.parse(stored);
                const map = Array.isArray(parsed) ? new Map(parsed) : new Map(Object.entries(parsed));
                if (map.has(verifiedSelectedEvent.id)) {
                    // Update the ref and return the problem ID
                    eventProblemMapRef.current = map;
                    return map.get(verifiedSelectedEvent.id);
                }
            }
        } catch (error) {
            console.error('Error loading event problem map:', error);
        }

        return null;
    };

    const problemIdForEvent = getProblemIdForEvent();

    // Find the specific problem that was created for this event
    // ONLY use the problem ID from localStorage - don't fallback to domain matching
    // This ensures each event shows only its own problem, not problems from other events with the same domain
    // Handle potential type mismatches (string vs number) by converting both to strings for comparison
    const problemForEvent = problemIdForEvent && state.problem && Array.isArray(state.problem)
        ? state.problem.find(obj => String(obj.id) === String(problemIdForEvent))  // Only use the specific problem ID, handle type mismatch
        : null;

    // Show ProblemView ONLY if we have a specific problem for this event (from localStorage mapping)
    // Don't show ProblemView if we only have problems by domain - each event needs its own problem
    const shouldShowProblemView = problemForEvent !== null && problemForEvent !== undefined;

    return (
        <>
            <AppBarWidget />
            <div id='problem-screen-top'>
                <PagesTitle title={'Problem'} />
            </div>
            <div id='problem-screen'>
                {shouldShowProblemView ?
                    <ProblemView problem={problemForEvent ? [problemForEvent] : []} event={verifiedSelectedEvent} /> :
                    <CreateProblemWidget
                        projectSelected={state}
                        selectedEvent={verifiedSelectedEvent}
                        onProblemCreated={(problemId) => {
                            // Mark that a problem was created for this event and store the problem ID
                            const getStorageKey = () => `eventProblems_${state?.id || 'default'}`;
                            const saveEventProblemMap = (map) => {
                                try {
                                    localStorage.setItem(getStorageKey(), JSON.stringify(Array.from(map.entries())));
                                } catch (error) {
                                    console.error('Failed to save event problem map:', error);
                                }
                            };
                            // Store the problem ID so we can find the exact problem later
                            eventProblemMapRef.current.set(verifiedSelectedEvent.id, problemId);
                            saveEventProblemMap(eventProblemMapRef.current);
                        }}
                    />
                }
            </div>
        </>
    )
}